import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Bell, BellOff, LogOut, ShieldCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Dashboard from "@/components/reko/Dashboard";
import ClientsTab from "@/components/reko/ClientsTab";
import AnalyticsTab from "@/components/reko/AnalyticsTab";
import UsersTab from "@/components/reko/UsersTab";
import Module1 from "@/components/reko/Module1";
import Module2 from "@/components/reko/Module2";
import Module3 from "@/components/reko/Module3";
import Module4 from "@/components/reko/Module4";
import AuditTrail from "@/components/reko/AuditTrail";
import DeviationModal from "@/components/reko/DeviationModal";

const TABS = [
  { id: "dashboard", label: "Översikt" },
  { id: "kunder", label: "Kunder & Uppdrag" },
  { id: "modul1", label: "Modul 1: Analys & Kvalitetsmål" },
  { id: "modul2", label: "Modul 2: Riskbedömning" },
  { id: "modul3", label: "Modul 3: Åtgärder & Checklistor" },
  { id: "modul4", label: "Modul 4: Årlig Utvärdering" },
  { id: "anvandare", label: "Användare", managerOnly: true },
  { id: "statistik", label: "Statistik & PDF" },
  { id: "audit", label: "Audit Trail" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [devModal, setDevModal] = useState({ open: false, checklistId: null });
  const [addUserSignal, setAddUserSignal] = useState(0);
  const [notif, setNotif] = useState(user.notifications_enabled !== false);

  const toggleNotif = async () => {
    const next = !notif;
    setNotif(next);
    try {
      await api.put("/users/me/notifications", { enabled: next });
      toast.success(next ? "E-postnotiser aktiverade" : "E-postnotiser avstängda");
    } catch (e) {
      toast.error(formatApiError(e));
      setNotif(!next);
    }
  };

  const loadDash = useCallback(() => {
    api.get("/dashboard").then((r) => setDash(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadDash(); }, [loadDash]);

  const showAlert = dash && (dash.deviations.olosta_generella > 0 || dash.risk_analysis.status !== "green");
  const isManager = ["Kvalitetsansvarig", "Byråledning", "Supervisor"].includes(user.role);
  const tabs = TABS.filter((t) => !t.managerOnly || isManager);

  return (
    <div className="bg-slate-50 text-slate-800 min-h-screen flex flex-col" data-testid="app-shell">
      <header className="bg-slate-900 text-white sticky top-0 z-40 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm font-extrabold leading-tight">Redoc QMS</div>
                <div className="text-[11px] text-slate-400 leading-tight">Kvalitetsstyrningssystem · Exempelbyrån AB</div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {isManager && (
                <button
                  data-testid="add-employee-btn"
                  onClick={() => { setTab("anvandare"); setAddUserSignal((s) => s + 1); }}
                  className="flex items-center text-xs font-bold bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg shadow transition-colors"
                >
                  <UserPlus className="w-3.5 h-3.5 mr-1.5" />Lägg till medarbetare
                </button>
              )}
              <div className="text-right hidden sm:block">
                <div className="text-xs font-bold" data-testid="header-user-name">{user.name}</div>
                <div className="text-[11px] text-blue-300" data-testid="header-user-role">{user.role}</div>
              </div>
              <button
                data-testid="notifications-toggle-btn" onClick={toggleNotif}
                title={notif ? "E-postnotiser: PÅ — klicka för att stänga av" : "E-postnotiser: AV — klicka för att aktivera"}
                className={`flex items-center px-2.5 py-1.5 rounded-lg transition-colors ${notif ? "text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700" : "text-slate-500 bg-slate-800/50"}`}
              >
                {notif ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
              </button>
              <button
                data-testid="logout-btn" onClick={logout}
                className="flex items-center text-xs font-semibold text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 px-3 py-1.5 rounded-lg transition-colors"
              >
                <LogOut className="w-3.5 h-3.5 mr-1.5" />Logga ut
              </button>
            </div>
          </div>
          <nav className="flex space-x-1 overflow-x-auto pb-0 -mb-px">
            {tabs.map((t) => (
              <button
                key={t.id}
                data-testid={`nav-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`whitespace-nowrap text-xs font-semibold px-4 py-2.5 rounded-t-lg transition-colors ${
                  tab === t.id ? "bg-slate-50 text-slate-900" : "text-slate-400 hover:text-white hover:bg-slate-800"
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-grow w-full">
        {showAlert && (
          <div data-testid="alert-banner" className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r shadow-sm flex items-start justify-between mb-6">
            <div className="flex items-start">
              <AlertTriangle className="text-amber-600 w-5 h-5 mr-3 mt-0.5" />
              <div>
                <h3 className="text-sm font-bold text-amber-800">Uppmärksamhet krävs (Reko 220/225)</h3>
                <p className="text-sm text-amber-700 mt-0.5">
                  {dash.deviations.olosta_generella > 0 && (
                    <>Det finns <strong>{dash.deviations.olosta_generella} olöst{""}
                    {dash.deviations.olosta_generella > 1 ? "a" : ""} generell{""}
                    {dash.deviations.olosta_generella > 1 ? "a" : ""} kvalitetsbrist{""}
                    {dash.deviations.olosta_generella > 1 ? "er" : ""}</strong> loggad. </>
                  )}
                  {dash.risk_analysis.status !== "green" && (
                    <>Riskanalysen kräver årlig utvärdering{dash.risk_analysis.days_left != null && dash.risk_analysis.days_left > 0 ? ` inom ${dash.risk_analysis.days_left} dagar` : " omedelbart"}. </>
                  )}
                  Årsstängning i Modul 4 är spärrad för slutsats A tills bristerna hanterats.
                </p>
              </div>
            </div>
            <button
              data-testid="alert-goto-modul4-btn"
              onClick={() => setTab("modul4")}
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded shadow whitespace-nowrap ml-4"
            >
              Gå till Utvärdering
            </button>
          </div>
        )}

        {tab === "dashboard" && <Dashboard dash={dash} refresh={loadDash} onReportDeviation={() => setDevModal({ open: true, checklistId: null })} onOpenChecklists={() => setTab("modul3")} />}
        {tab === "kunder" && <ClientsTab />}
        {tab === "modul1" && <Module1 />}
        {tab === "modul2" && <Module2 />}
        {tab === "modul3" && <Module3 onReportDeviation={(cid) => setDevModal({ open: true, checklistId: cid })} refreshDash={loadDash} />}
        {tab === "modul4" && <Module4 refreshDash={loadDash} />}
        {tab === "anvandare" && <UsersTab autoOpenSignal={addUserSignal} />}
        {tab === "statistik" && <AnalyticsTab />}
        {tab === "audit" && <AuditTrail />}
      </main>

      <footer className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-[11px] text-slate-400">
        <div className="text-center sm:text-left">
          Kvalitetsstyrningssystem enligt FAR Reko 200–230 · Oföränderlig audit trail aktiverad
          <div className="mt-1" data-testid="footer-credit">
            © Redoc TM AB · Web by <a href="https://logartgroup.com/" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-blue-600 underline underline-offset-2 transition-colors" data-testid="footer-credit-link">Logardt Group</a>
          </div>
        </div>
        <nav className="flex items-center gap-4" data-testid="footer-legal-links">
          <Link to="/villkor" data-testid="footer-terms-link" className="text-slate-500 hover:text-blue-600 transition-colors">Användarvillkor</Link>
          <Link to="/integritet" data-testid="footer-privacy-link" className="text-slate-500 hover:text-blue-600 transition-colors">Integritetspolicy</Link>
          <Link to="/cookies" data-testid="footer-cookies-link" className="text-slate-500 hover:text-blue-600 transition-colors">Cookiepolicy</Link>
        </nav>
      </footer>

      {devModal.open && (
        <DeviationModal
          checklistId={devModal.checklistId}
          onClose={() => setDevModal({ open: false, checklistId: null })}
          onSaved={() => { setDevModal({ open: false, checklistId: null }); loadDash(); }}
        />
      )}
    </div>
  );
}
